// Deleting many records at once (admins). A list puts a tick box on each row (shift-click
// ticks a range), a bar above it selects all, none, the inverse or a preset ("debug
// patients"), and "Delete selected…" opens a panel that asks the server what would go:
// totals, what is refused and why, and an option to take whatever still uses a record
// along with it. Nothing goes until "delete N" is typed.

import { useEffect, useRef, useState } from "react";
import { api, type DeleteKind, type DeletePreview, type Targets } from "../api";
import { errorText } from "../hooks";

export const rowKey = (kind: DeleteKind, id: number) => `${kind}:${id}`;

const NOUNS: Record<DeleteKind | "trials", [string, string]> = {
  patients: ["patient", "patients"], sessions: ["session", "sessions"], tests: ["test", "tests"],
  trials: ["trial", "trials"], programs: ["program", "programs"], sites: ["site", "sites"],
  stations: ["station", "stations"], displays: ["display", "displays"],
  display_models: ["approved monitor", "approved monitors"], users: ["account or login", "accounts and logins"],
};

export function plural(n: number, kind: DeleteKind | "trials"): string {
  const [one, many] = NOUNS[kind];
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

export interface Selection {
  keys: Set<string>;
  size: number;
  has: (k: string) => boolean;
  /** A tick box was clicked. With shift, every row between the last click and this one in
   *  `order` (the rows as shown) takes this row's new state. */
  click: (k: string, order: string[], shift: boolean) => void;
  set: (keys: string[], on: boolean) => void;
  only: (keys: string[]) => void;
  clear: () => void;
  targets: () => Targets;
}

export function useSelection(): Selection {
  const [keys, setKeys] = useState<Set<string>>(() => new Set());
  const anchor = useRef<string | null>(null);
  const set = (ks: string[], on: boolean) => setKeys((prev) => {
    const next = new Set(prev);
    for (const k of ks) { if (on) next.add(k); else next.delete(k); }
    return next;
  });
  return {
    keys,
    size: keys.size,
    has: (k) => keys.has(k),
    click: (k, order, shift) => {
      const on = !keys.has(k);
      const a = anchor.current ? order.indexOf(anchor.current) : -1;
      const b = order.indexOf(k);
      if (shift && a >= 0 && b >= 0) set(order.slice(Math.min(a, b), Math.max(a, b) + 1), on);
      else set([k], on);
      anchor.current = k;
    },
    set,
    only: (ks) => { anchor.current = null; setKeys(new Set(ks)); },
    clear: () => { anchor.current = null; setKeys(new Set()); },
    targets: () => {
      const t: Targets = {};
      for (const k of keys) {
        const [kind, id] = k.split(":") as [DeleteKind, string];
        (t[kind] ??= []).push(Number(id));
      }
      return t;
    },
  };
}

/** A row's tick box. */
export function RowCheck({ sel, k, order, label, disabled, checked }: {
  sel: Selection; k: string; order: string[]; label: string; disabled?: boolean; checked?: boolean;
}) {
  return (
    <input type="checkbox" className="pick" aria-label={label} disabled={disabled}
      checked={checked ?? sel.has(k)}
      onChange={(e) => sel.click(k, order, (e.nativeEvent as MouseEvent).shiftKey === true)}
      onClick={(e) => e.stopPropagation()} />
  );
}

/** The header tick box: all rows shown, or none. */
export function AllCheck({ sel, keys, label = "Select all shown" }: { sel: Selection; keys: string[]; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const n = keys.filter((k) => sel.has(k)).length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = n > 0 && n < keys.length; }, [n, keys.length]);
  return (
    <input ref={ref} type="checkbox" className="pick" aria-label={label} disabled={!keys.length}
      checked={keys.length > 0 && n === keys.length} onChange={() => sel.set(keys, n !== keys.length)} />
  );
}

export interface Preset { label: string; keys: string[] }

/** The bar above a list: what is selected, quick selections, and Delete selected. */
export function BulkBar({ sel, shown, presets = [], onDeleted, what = "rows" }: {
  sel: Selection; shown: string[]; presets?: Preset[]; onDeleted: () => void; what?: string;
}) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const hidden = [...sel.keys].filter((k) => !shown.includes(k)).length;
  const invert = () => {
    const on = shown.filter((k) => !sel.has(k));
    sel.set(shown, false);
    sel.set(on, true);
  };
  return (
    <div className="bulk">
      <div className="bulk-bar" role="toolbar" aria-label="Select and delete">
        {sel.size === 0
          ? <span className="muted small">Tick {what} to delete several at once (shift-click ticks a range).</span>
          : <strong>{sel.size} selected{hidden > 0 && <span className="warn small"> ({hidden} not shown by the filter)</span>}</strong>}
        <span className="bulk-actions">
          <button className="link" disabled={!shown.length} onClick={() => sel.set(shown, true)}>All {shown.length} shown</button>
          <button className="link" disabled={!sel.size} onClick={sel.clear}>None</button>
          <button className="link" disabled={!shown.length} onClick={invert}>Invert</button>
          {presets.map((p) => (
            <button key={p.label} className="link" disabled={!p.keys.length} onClick={() => sel.only(p.keys)}>
              {p.label} ({p.keys.length})
            </button>
          ))}
        </span>
        <button className="danger" disabled={!sel.size || open} onClick={() => { setDone(null); setOpen(true); }}>
          Delete selected…
        </button>
      </div>
      {done && <div className="small good" role="status">{done}</div>}
      {open && (
        <DeletePanel targets={sel.targets()} onCancel={() => setOpen(false)}
          onDeleted={(msg) => { setOpen(false); setDone(msg); sel.clear(); onDeleted(); }} />
      )}
    </div>
  );
}

function totalsText(t: DeletePreview["totals"]): string[] {
  const out: string[] = [];
  if (t.patients) out.push(plural(t.patients, "patients"));
  if (t.sessions) {
    out.push(`${plural(t.sessions, "sessions")}${t.tests ? ` with ${plural(t.tests, "tests")}` : ""}`
      + `${t.trials ? ` (${plural(t.trials, "trials")})` : ""}`);
  } else if (t.tests) {
    out.push(`${plural(t.tests, "tests")}${t.trials ? ` (${plural(t.trials, "trials")})` : ""}`);
  }
  for (const k of ["users", "programs", "sites", "stations", "displays", "display_models"] as const) {
    if (t[k]) out.push(plural(t[k], k));
  }
  return out;
}

export function DeletePanel({ targets, onCancel, onDeleted }: {
  targets: Targets; onCancel: () => void; onDeleted: (message: string) => void;
}) {
  const [cascade, setCascade] = useState(false);
  const [p, setP] = useState<DeletePreview | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tkey = JSON.stringify(targets);

  useEffect(() => {
    let live = true;
    setP(null);
    setError(null);
    api.deletePreview(JSON.parse(tkey) as Targets, cascade).then(
      (r) => { if (live) setP(r); }, (e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [tkey, cascade]);

  const go = async () => {
    if (!p) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.deleteMany(targets, cascade, typed, p.fingerprint);
      const lines = totalsText({ ...r.deleted, trials: 0 } as DeletePreview["totals"]);
      onDeleted(`Deleted ${lines.join(", ") || "nothing"}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const ok = !!p && p.will_delete > 0 && typed.trim().toLowerCase() === p.confirm_phrase;
  const stay = p?.sessions_of_patients_who_stay;
  return (
    <div className="card delete-panel" role="dialog" aria-label="Delete selected">
      <h3>Delete permanently</h3>
      {!p && !error && <p className="muted">Working out what would go…</p>}
      {p && (
        <>
          {p.will_delete > 0 ? (
            <>
              <p>{p.will_delete === p.selected ? `All ${p.selected}` : `${p.will_delete} of the ${p.selected}`} selected
                will be deleted, with everything that belongs to them:</p>
              <ul className="totals">{totalsText(p.totals).map((l) => <li key={l}>{l}</li>)}</ul>
            </>
          ) : <p className="warn">None of the {p.selected} selected can be deleted as things stand.</p>}
          {stay && stay.sessions > 0 && (
            <p className="bad">This includes {plural(stay.sessions, "sessions")} of {plural(stay.patients, "patients")} who
              are not being deleted: those results leave their history.</p>
          )}
          {p.notes.length > 0 && <ul className="small">{p.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
          {p.refused.length > 0 && (
            <details open={p.refused.length <= 6} className="refused">
              <summary className="warn">{p.refused.length} can’t be deleted</summary>
              <ul className="small">
                {p.refused.map((r) => <li key={`${r.kind}-${r.id}`}><strong>{NOUNS[r.kind][0]} {r.label}</strong>: {r.reason}</li>)}
              </ul>
            </details>
          )}
          {(p.cascade_would_help || cascade) && (
            <label className="inline">
              <input type="checkbox" checked={cascade} onChange={(e) => { setTyped(""); setCascade(e.target.checked); }} />
              Also delete whatever still uses them (sessions run on a station or display, a site’s patients,
              accounts and stations, the sessions an account ran)
            </label>
          )}
          {p.will_delete > 0 && (
            <div className="row" style={{ marginTop: "0.6rem" }}>
              <label><span>Type <code>{p.confirm_phrase}</code> to confirm</span>
                <input aria-label="Confirm delete" value={typed} onChange={(e) => setTyped(e.target.value)}
                  placeholder={p.confirm_phrase} autoComplete="off" />
              </label>
              <button className="danger" disabled={!ok || busy} onClick={() => void go()}>
                {busy ? "Deleting…" : `Delete ${p.will_delete} permanently`}
              </button>
            </div>
          )}
        </>
      )}
      {error && <div className="error">{error}</div>}
      <div className="row" style={{ marginTop: "0.6rem" }}>
        <button className="secondary" onClick={onCancel}>Cancel</button>
        <span className="small muted">Deleted records cannot be brought back (except from the server’s nightly
          backup). Each one is written to the audit log.</span>
      </div>
    </div>
  );
}
